import { ExtraArguments } from '../extraArguments';
import { State, Modals } from '../state';
import { Dispatch } from 'redux';
import { patch } from './patch';
import up = require('updeep');
import { Entity, PledgeCompletions, PledgePoints, CompetitionUserPoint } from '../../userApi';
import logger from '../../utils/logger';
import { getCompetitionUserScore } from '../../utils/userApiHelper';

interface Pointable {
    points?: number;
    savingsCO2Lbs?: number;
    savingsWaterOz?: number;
    savingsGasGallons?: number;
    savingsElectricityKwh?: number;
}

const addPointsToScore = <T extends Pointable>(points: Pointable, score: T | undefined): T => ({
  ...(score || {} as T),
  points: ((score && score.points) || 0) + (points.points || 0),
  savingsCO2Lbs: ((score && score.savingsCO2Lbs) || 0) + (points.savingsCO2Lbs || 0),
  savingsWaterOz: ((score && score.savingsWaterOz) || 0) + (points.savingsWaterOz || 0),
  savingsGasGallons: ((score && score.savingsGasGallons) || 0) + (points.savingsGasGallons || 0),
  savingsElectricityKwh: ((score && score.savingsElectricityKwh) || 0) + (points.savingsElectricityKwh || 0)
});

export const dismissStoryPointsModal = () => patch({
  modal: Modals.None,
  lastPointsEarned: null
});

export const addUserPoints = (points: CompetitionUserPoint, pledgeID?: number, carbon?: number) => async (dispatch: Dispatch, getState: () => State, extra: ExtraArguments) => {
  const {
    user, userPledgeCompletions, userPledgePoints, entities, competition,
    competitionEntityScores, competitionUserScores, competitionScore, competitionPledges
  } = getState();
  const competitionUserScore = getCompetitionUserScore(competitionUserScores, user.id);
  const completionUpdates: PledgeCompletions = {};
  const pledgePointsUpdates: PledgePoints = {};
  if (pledgeID) {
    completionUpdates[pledgeID] = (userPledgeCompletions[pledgeID] || 0) + 1;
    pledgePointsUpdates[pledgeID] = (userPledgePoints[pledgeID] || 0) + points.points;
  }

  dispatch(patch({
    competitionScore: {
      ...competitionScore,
      ...addPointsToScore(points, competitionScore)
    },
    competitionUserScores: {
      [user.id]: {
        ...competitionUserScore,
        ...addPointsToScore(points, competitionUserScore)
      }
    },
    userPledgeCompletions: {
      ...(userPledgeCompletions || {}),
      ...completionUpdates
    },
    userPledgePoints: {
      ...(userPledgePoints || {}),
      ...pledgePointsUpdates
    },
    lastPointsEarned: points,
    lastPointsCanEarnMore: pledgeID
      ? completionUpdates[pledgeID] < competitionPledges[pledgeID].repeats
      : false
  }));

  // Update user's entity scores
  const entityIDs = Object.keys(user.acceptedCompetitionEntityMemberships[competition.id]);
  entityIDs.forEach((entityID) => {
    const entity = entities[entityID as any];
    if (!entity) return;
    const entityType = entity.type;
    if (entityType === Entity.TypeEnum.Team) {
      const index = competitionEntityScores.team.findIndex((ces) => ces.entityID === Number(entityID));
      if (index === -1) {
        logger.error(`addUserPoints: Could not find team CompetitionEntityScore for entityID: ${entityID}.`);
        return;
      }
      dispatch(patch({
        competitionEntityScores: {
          ...competitionEntityScores,
          team: up.updateIn(`${index}`, (existing) => addPointsToScore(points, existing), competitionEntityScores.team)
        }
      }));
    } else if (entityType === Entity.TypeEnum.University) {
      const index = competitionEntityScores.university.findIndex((ces) => ces.entityID === Number(entityID));
      if (index === -1) {
        logger.error(`addUserPoints: Could not find university CompetitionEntityScore for entityID: ${entityID}.`);
        return;
      }
      dispatch(patch({
        competitionEntityScores: {
          ...competitionEntityScores,
          university: up.updateIn(`${index}`, (existing) => addPointsToScore(points, existing), competitionEntityScores.university)
        }
      }));
    } else if (entityType === Entity.TypeEnum.HealthFacility) {
      const index = competitionEntityScores.healthFacility.findIndex((ces) => ces.entityID === Number(entityID));
      if (index === -1) {
        logger.error(`addUserPoints: Could not find healthFacility CompetitionEntityScore for entityID: ${entityID}.`);
        return;
      }
      dispatch(patch({
        competitionEntityScores: {
          ...competitionEntityScores,
          healthFacility: up.updateIn(`${index}`, (existing) => addPointsToScore(points, existing), competitionEntityScores.healthFacility)
        }
      }));
    } else {
      logger.error(`Unexpected entity type when adding user points: ${entityType}`);
    }
  });
};
